/**
 * The toast queue and the root it renders into.
 *
 * The library ships no provider and holds no queue -- deliberately, because this host mounts every
 * block as its own root and a context could not span them. `LoyaltyToastViewport` is prop-fed: it
 * takes an array and an onDismiss, and the array belongs here.
 *
 * **No component, no hooks.** Preact's render() into the same container diffs, so the viewport
 * instance and the portal it owns persist across pushes; the array lives in module scope. Wrapping
 * this in a <ToastLayer/> with useState would model a lifecycle that does not exist -- this layer
 * has no props, no parent, no context and no unmount. joinStripReveal.js and urcAuth.js decline
 * hooks for the same reason.
 *
 * The container goes on document.body, never inside <main>. The viewport portals to body either
 * way, so this is not about where the toasts paint: it is that <main> lives inside #app, in a
 * subtree the theme's AJAX partials replace wholesale, and a replaced subtree silently detaches
 * this root while its portal content stays in the document -- leaving live toasts driven by an
 * orphan. body cannot be missing and cannot be replaced.
 */

import { render } from 'preact';
import { LoyaltyToastViewport } from '@drewling/urc-loyalty-ui';
import { resolveBadgeIcon } from '../badgeArtwork';
import './toast.css';

/** The host-owned queue. Newest last; the viewport reverses it for display. */
let toasts = [];

/** The element this layer renders into. Non-null once mounted -- also the once-only guard. */
let container = null;

/**
 * A badge toast's `badge` is the `BadgeDetail` trigger.js read through the shared client, so it
 * carries the API artwork like every other surface's; resolveBadgeIcon picks the earned picture.
 *
 * @return {void}
 */
function paint() {
	render(
		<LoyaltyToastViewport
			toasts={ toasts }
			onDismiss={ dismiss }
			resolveBadgeIcon={ resolveBadgeIcon }
		/>,
		container
	);
}

/**
 * @param {string} id The id the panel reported once its exit animation finished.
 * @return {void}
 */
function dismiss( id ) {
	toasts = toasts.filter( toast => toast.id !== id );

	paint();
}

/**
 * Create the root and render the viewport with an EMPTY queue.
 *
 * The empty first commit is load-bearing, not an initialisation detail. The viewport's live region
 * has to already exist in the accessibility tree before its contents change -- a node that appears
 * carrying both role="status" and its text in one commit is announced unreliably across screen
 * readers. Every caller reaches this before its first network round trip, so the region pre-exists
 * its contents by hundreds of milliseconds.
 *
 * Idempotent: a second call is a no-op rather than a second layer.
 *
 * @return {void}
 */
export function mountToastLayer() {
	if ( container ) {
		return;
	}

	container = document.createElement( 'div' );
	container.dataset.loyaltyToastRoot = '';
	document.body.appendChild( container );

	paint();
}

/**
 * Append toasts the reconciler minted, skipping any already on screen.
 *
 * Ids are derived from the event (`tier:<name>`, `badge:<id>`), so a double reconcile is idempotent
 * and this filter is what makes it so.
 *
 * @param {Array<Object>} items Fresh toast items.
 * @return {void}
 */
export function pushToasts( items ) {
	if ( ! container || items.length === 0 ) {
		return;
	}

	const fresh = items.filter( item => ! toasts.some( existing => existing.id === item.id ) );

	if ( fresh.length === 0 ) {
		return;
	}

	toasts = [ ...toasts, ...fresh ];

	paint();
}

/**
 * Take every toast off screen.
 *
 * For the account-switch reset, and for nothing else. A toast congratulating fan A must not be left
 * in front of fan B -- these are dismissible-on-click but auto-dismiss after six seconds of
 * UNATTENDED life, so one being hovered or focused at the moment of the switch stays indefinitely.
 *
 * A repaint rather than a teardown: the viewport's live region has to keep existing, for the reason
 * `mountToastLayer()`'s empty first commit describes. Unmounting and remounting it would put a
 * `role="status"` node back into the accessibility tree carrying content, which is what that
 * ordering exists to avoid.
 *
 * @return {void}
 */
export function clearToasts() {
	if ( ! container || toasts.length === 0 ) {
		return;
	}

	toasts = [];

	paint();
}
